import { isTicketBookable } from '@/lib/api.ts';
import type { PaymentType } from '@/lib/types.ts';

export interface AccessType {
  id: number;
  name: string;
  description?: string;
  coming_soon?: boolean;
  price_summary?: string;
  guest_summary?: string;
  sold_out?: boolean;
}

export interface TicketItem {
  id: number;
  price: number;
  price__currency: string;
  sold_out: boolean;
  guests: number;
  maximum_purchasable: number;
  has_tiered_pricing?: boolean;
  tiers?: Array<{ id: string; is_sold_out: boolean }>;
  allowedPaymentTypes?: PaymentType[];
  item: {
    name?: string;
    description?: string;
    coming_soon?: boolean;
    requires_idv?: boolean;
  };
}

// A venue-map target: shapes match against access-type OR ticket names, so one
// map works whether the zones live at the access-type or the ticket level.
// Ticket targets carry their owning access type so a map tap can switch to
// that zone.
export type EventVenueTarget =
  | { kind: 'access'; name: string; acc: AccessType }
  | { kind: 'ticket'; name: string; ticket: TicketItem; acc: AccessType };

// Map-shape candidates: EVERY access type AND every cached zone's tickets,
// bookable or not — a table is tappable whether or not its zone is the
// expanded one, and a sold-out section still matches its shape so it dims
// instead of vanishing. Bookability lives in the map's `isItemAvailable`.
// A cache from another event contributes no ticket targets.
export const buildEventVenueTargets = (input: {
  accessTypes: AccessType[];
  zoneInventory: { eventId: number; byAccessType: Record<number, TicketItem[]> } | null;
  selectedEventId: number | null;
}): EventVenueTarget[] => {
  const { accessTypes, zoneInventory, selectedEventId } = input;
  const targets: EventVenueTarget[] = [];
  for (const acc of accessTypes) {
    targets.push({ kind: 'access', name: acc.name, acc });
  }
  const byAccessType = zoneInventory?.eventId === selectedEventId ? zoneInventory.byAccessType : {};
  for (const acc of accessTypes) {
    for (const ticket of byAccessType[acc.id] ?? []) {
      targets.push({ kind: 'ticket', name: ticket.item?.name ?? '', ticket, acc });
    }
  }
  return targets;
};

// Which ticket a freshly loaded zone should select, in priority order:
// 1. A map tap's requested table — exactly that one if still bookable, else
//    null (never substitute another table).
// 2. The deep-linked / persisted ticket, when still present in the rows and
//    bookable — a table that sold out since it was persisted falls through.
// 3. The first bookable row (`isTicketBookable`: tier-aware, not coming soon).
export const resolveSelectedTicketId = (input: {
  inventory: TicketItem[];
  /** A map tap's explicitly requested table; wins over everything when bookable. */
  selectTicketId?: number | null;
  /** The deep-linked / persisted ticket for this event, restored when still present and bookable. */
  persistedTicketId?: number | null;
}): number | null => {
  const { inventory, selectTicketId, persistedTicketId } = input;
  if (selectTicketId != null) {
    const requested = inventory.find(
      (ticket) => ticket.id === selectTicketId && isTicketBookable(ticket)
    );
    return requested?.id ?? null;
  }
  if (
    persistedTicketId &&
    inventory.some((ticket) => ticket.id === persistedTicketId && isTicketBookable(ticket))
  ) {
    return persistedTicketId;
  }
  // Tier-aware via `isTicketBookable`, the same rule the map, the list and
  // `handleTicketSelect` use: a tiered row's own `sold_out` can lag its tiers,
  // so the parent flag alone would auto-select a row the list then refuses
  // (every tier gone) or skip one it would book (an open tier).
  const firstAvailable = inventory.find((ticket) => isTicketBookable(ticket));
  return firstAvailable?.id ?? null;
};
